import Tippy from '@tippyjs/react'
import { useEffect, useState, KeyboardEvent, useRef } from 'react'
import {
    EventInputWrapper,
    EventTrigger,
    Field,
    Wrapper,
} from 'components/shared/Input/styles'
import { Option, Options, TippyWrapper } from './styles'
import Box from 'components/shared/Box'
import Text from 'components/shared/Text'
import Icon from 'components/shared/Icon'
import { HourDropdownText } from 'components/shared/Text/HourDropdownText'

export interface OptionProps {
    option: string
    optionInfo?: string
    optionData?: number
    isDisabled?: boolean
    value?: string
}

interface SelectProps {
    value: string | null
    options: OptionProps[] | null
    id: string
    placeholder?: string
    onChange: (value: string) => void
    label?: string
    isRequired?: boolean
    isError?: boolean
    errorMessage?: string
    hasNoMaxWidth?: boolean
    isLoading?: boolean
    isDisabled?: boolean
    isHoursSelect?: boolean
    testId?: string
    isPortal?: boolean
}

const Select = ({
    options,
    placeholder,
    id,
    label,
    isRequired = false,
    onChange,
    isError,
    errorMessage,
    hasNoMaxWidth = false,
    isHoursSelect = false,
    value,
    isLoading,
    isDisabled,
    testId,
    isPortal,
}: SelectProps) => {
    const [isOpened, setIsOpened] = useState(false)
    const [fieldValue, setFieldValue] = useState('')

    const areOptionsFetched = options && options[0].option !== ''

    const handleChooseOption = (option: string, index: number) => {
        onChange(option)
        setIsOpened(false)
    }

    const fieldValueHandler = () => {
        setFieldValue(value ?? '')
        if (!value || !options) return
        const isValueIncluded = Object.keys(options[0]).includes('value')

        const currentOption = options.find(
            (option) => option[isValueIncluded ? 'value' : 'option'] === value
        )
        if (currentOption) setFieldValue(currentOption.option)
    }

    const handleSelectKeyDown = (event: KeyboardEvent<HTMLDivElement>) =>
        event.key === 'Enter' && !isDisabled && !isOpened && setIsOpened(true)

    useEffect(() => {
        if (!options) return
        placeholder || setFieldValue(options[0].option)
        fieldValueHandler()
    }, [areOptionsFetched, value])

    const boxRef = useRef<HTMLDivElement | null>(null)

    return (
        <Box gap={4} isFullWidth ref={boxRef}>
            {!!label && (
                <Text
                    as="label"
                    variant="bodySmall"
                    htmlFor={id}
                    color={isError ? 'error' : 'grayscale_900'}
                    hasTransition
                >
                    {label} {isRequired && '*'}
                </Text>
            )}

            <TippyWrapper tabIndex={0} onKeyDown={handleSelectKeyDown}>
                <Tippy
                    content={
                        <Options
                            parentWidth={
                                isPortal && boxRef?.current
                                    ? boxRef.current.clientWidth
                                    : undefined
                            }
                        >
                            {options?.map((item, index) => {
                                const {
                                    option,
                                    optionInfo,
                                    isDisabled,
                                    value,
                                } = item

                                const dropdownOptionColor = isDisabled
                                    ? 'grayscale_500'
                                    : 'grayscale_900'

                                if (!option) return null

                                return (
                                    <Option
                                        key={option}
                                        onClick={
                                            isDisabled
                                                ? undefined
                                                : () => {
                                                      handleChooseOption(
                                                          value || option,
                                                          index
                                                      )
                                                  }
                                        }
                                        onKeyDown={
                                            isDisabled
                                                ? undefined
                                                : (
                                                      event: KeyboardEvent<HTMLSpanElement>
                                                  ) =>
                                                      event.key === 'Enter' &&
                                                      handleChooseOption(
                                                          value || option,
                                                          index
                                                      )
                                        }
                                        isDisabled={isDisabled}
                                        data-testid={`${testId}-${
                                            value || option
                                        }`}
                                        tabIndex={0}
                                    >
                                        <Box gap={8} direction="row">
                                            {isHoursSelect ? (
                                                <HourDropdownText
                                                    hasNoWrap
                                                    variant="body"
                                                    color={dropdownOptionColor}
                                                    align="left"
                                                >
                                                    {option}
                                                </HourDropdownText>
                                            ) : (
                                                <Text
                                                    variant="body"
                                                    color={dropdownOptionColor}
                                                    align="left"
                                                >
                                                    {option}
                                                </Text>
                                            )}

                                            {!!optionInfo && (
                                                <Text
                                                    variant="body"
                                                    color="grayscale_500"
                                                >
                                                    {optionInfo}
                                                </Text>
                                            )}
                                        </Box>
                                    </Option>
                                )
                            })}
                        </Options>
                    }
                    appendTo={isPortal ? () => document.body : 'parent'}
                    interactive={true}
                    visible={isOpened}
                    onClickOutside={() => setIsOpened(false)}
                    offset={[0, 4]}
                    placement="bottom-start"
                    delay={0}
                    duration={[0, 0]}
                >
                    <EventInputWrapper>
                        <Wrapper hasNoMaxWidth={hasNoMaxWidth}>
                            <Field
                                type="text"
                                id={id}
                                placeholder={placeholder}
                                disabled={true}
                                required={isRequired}
                                isError={isError}
                                value={
                                    options?.find(
                                        (option) => option.value === fieldValue
                                    )?.option || fieldValue
                                }
                                isCustomField
                                isDisabled={isDisabled}
                            />
                            {isLoading ? (
                                <Icon
                                    variant="spinner"
                                    size={14}
                                    isSpinning={true}
                                />
                            ) : (
                                <Icon
                                    variant="chevron"
                                    rotation={isOpened ? 180 : 0}
                                />
                            )}
                        </Wrapper>
                        <EventTrigger
                            onClick={() =>
                                !isDisabled &&
                                setIsOpened((isOpened) => !isOpened)
                            }
                            data-testid={testId}
                        />
                    </EventInputWrapper>
                </Tippy>
            </TippyWrapper>

            {isError && !!errorMessage && (
                <Text variant="bodyExtraSmall" color="error">
                    {errorMessage}
                </Text>
            )}
        </Box>
    )
}

export default Select
